<script setup lang="ts">
import { HttpStatusCode } from '@/api/results'

type ErrorEntity = { title: HttpStatusCode | string; description: string }

const ERROR_ENTITIES: Record<number | string, ErrorEntity> = {
  [HttpStatusCode.NOT_FOUND]: {
    title: HttpStatusCode.NOT_FOUND,
    description: 'Страница не найдена',
  },
  [HttpStatusCode.INTERNAL_SERVER_ERROR]: {
    title: 'Прием заказов временно приостановлен',
    description: 'Приносим извинения за неудобства',
  },
}

const error = useError()

const { go } = useRouter()

const refreshPage = () => go(0)

const errorEntity = computed(() => {
  if (!error.value) return

  return ERROR_ENTITIES[error.value?.statusCode]
})

const dataTestId = computed(() => {
  return error.value?.statusCode
    ? `error-${error.value?.statusCode}`
    : 'error-something'
})

const titleClasses = computed(() => {
  /* Для числовых отображений добавляем класс с другими стилями */
  return [
    'error__title',
    { 'error__title--code': typeof errorEntity?.value?.title === 'number' },
  ]
})
</script>

<template>
  <div v-if="errorEntity" :data-test-id="dataTestId" class="error">
    <div class="error__content">
      <the-illustration
        alt="Ошибка страницы"
        class="error__illustration"
        name="something-went-wrong"
      />

      <h2 :class="titleClasses">
        {{ errorEntity.title }}
      </h2>

      <div v-if="errorEntity.description" class="error__description">
        {{ errorEntity.description }}
      </div>

      <div class="error__actions">
        <the-button block variant="tertiary" href="/">На главную</the-button>

        <the-button block variant="primary" @click="refreshPage">
          Обновить
        </the-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: inherit;
  padding: 32px 20px;
  text-align: center;

  @include media('>=desktop-mini') {
    padding: 40px;
  }

  &__content {
    display: flex;
    flex-flow: column nowrap;
    align-items: center;
    justify-items: flex-start;
    width: 100%;
  }

  &__illustration {
    width: 160px;
    height: 160px;

    @include media('>tablet') {
      width: 200px;
      height: 200px;
    }
  }

  &__title {
    margin-top: 20px;
    font-size: 18px;
    font-weight: 800;
    line-height: 24px;

    @include media('>tablet') {
      font-size: 20px;
      line-height: 26px;
    }

    &--code {
      font-size: 28px;
      font-weight: 900;
      line-height: 34px;

      @include media('>tablet') {
        font-size: 36px;
        line-height: 44px;
      }
    }
  }

  &__description {
    margin-top: 12px;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    color: var(--ui-text-secondary);

    @include media('>tablet') {
      font-size: 16px;
      line-height: 20px;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    margin: 40px auto;

    @include media('>tablet') {
      width: 280px;
    }
  }
}
</style>
